import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit'
import { apiClient } from '@/services/api'

// Type assertion helper for API responses
const getResponseData = (response: any) => response.data as any

// Chart data interface for Ops Agent
export interface ChartData {
  type: 'chart'
  chartType: 'bar' | 'line' | 'pie' | 'doughnut' | 'table'
  title: string
  data: {
    labels: string[]
    datasets: Array<{
      label: string
      data: number[]
      backgroundColor?: string | string[]
      borderColor?: string | string[]
      borderWidth?: number
      fill?: boolean
    }>
  }
  options?: {
    responsive?: boolean
    maintainAspectRatio?: boolean
    plugins?: {
      legend?: {
        display?: boolean
        position?: 'top' | 'bottom' | 'left' | 'right'
      }
      title?: {
        display?: boolean
        text?: string
      }
    }
    scales?: {
      x?: {
        display?: boolean
        title?: {
          display?: boolean
          text?: string
        }
      }
      y?: {
        display?: boolean
        beginAtZero?: boolean
        title?: {
          display?: boolean
          text?: string
        }
      }
    }
    // Table-specific options (set by autoRenderTable)
    firstColumnHeader?: string
    columnGroups?: {
      groups: string[]
      metrics: string[]
    }
  }
  metadata?: {
    queryId?: string
    source?: string
    generatedAt?: string
  }
}

// Nested hierarchical node structure
export interface HierarchicalNode {
  id: string
  name: string
  type: 'store' | 'type' | 'department'
  metrics: Record<string, any>
  expanded: boolean
  children?: HierarchicalNode[]
}

// Hierarchical table data interface for Ops Agent (proper nested structure)
export interface HierarchicalTableData {
  type: 'hierarchical_table'
  title: string
  data: HierarchicalNode // Properly nested JSON structure
  metadata: {
    generatedAt: string
    source: string
    rowCount: number
    metricCount: number
    store: string
    week: number
    year: number
    orderedColumns?: string[] // Optional ordered columns from backend
  }
}

// Types specific to Ops Agent
export interface OpsMessage {
  id: string
  role: 'USER' | 'ASSISTANT'
  content: string
  createdAt: string
  isStreaming?: boolean
  error?: string
  charts?: ChartData[]
  hierarchicalTables?: HierarchicalTableData[]
  metadata?: {
    powerbi_queries?: Array<{
      query_type: string
      dataset: string
      row_count: number
      execution_time_ms: number
    }>
    charts?: Array<{
      chartId: string
      chartType: string
      title: string
      generatedAt: string
      dataPoints: number
      metadata?: any
    }>
    confidence?: number
  }
}

export interface OpsChatSession {
  id: string
  title: string | null
  isActive: boolean
  createdAt: string
  updatedAt: string
  messages: OpsMessage[]
  isLoading?: boolean
  error?: string
}

export interface OpsQueryResult {
  query_type: string
  dataset: string
  data: any[]
  metadata: {
    row_count: number
    execution_time_ms: number
    dataset_name: string
    tables_used: string[]
  }
}

interface OpsChatState {
  sessions: Record<string, OpsChatSession>
  activeSessionId: string | null
  loadingSessions: boolean
  sessionsError: string | null
  streaming: {
    enabled: boolean
    currentStreamingMessage: string | null
  }
}

const initialState: OpsChatState = {
  sessions: {},
  activeSessionId: null,
  loadingSessions: false,
  sessionsError: null,
  streaming: {
    enabled: true,
    currentStreamingMessage: null
  }
}

// Async thunks for Ops Agent
export const fetchOpsChatSessions = createAsyncThunk<OpsChatSession[], number>(
  'opsChat/fetchSessions',
  async (limit: number = 10, { rejectWithValue }) => {
    try {
      const response = await apiClient.get(`/ops/chat-sessions?limit=${limit}`)
      const data = getResponseData(response)

      // Handle different response structures
      if (data?.data) {
        return data.data
      } else if (Array.isArray(data)) {
        return data
      } else {
        console.warn('Unexpected response structure:', data)
        return []
      }
    } catch (error: any) {
      console.error('fetchOpsChatSessions error:', error)
      console.error('Error response:', error.response?.data)
      if (error.response?.status === 403) {
        return rejectWithValue('Access denied. Operations Agent requires System Administrator privileges.')
      }
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch sessions')
    }
  }
)

export const loadOpsChatSession = createAsyncThunk<OpsChatSession, string>(
  'opsChat/loadSession',
  async (sessionId: string, { rejectWithValue }) => {
    try {
      const response = await apiClient.get(`/ops/chat-sessions/${sessionId}`)
      const data = getResponseData(response)
      console.log('loadOpsChatSession full response:', response)
      console.log('loadOpsChatSession response.data:', data)
      console.log('loadOpsChatSession response.data.data:', data?.data)
      
      // Handle different response structures
      if (data?.data) {
        return data.data
      } else if (data && typeof data === 'object' && data.id) {
        return data
      } else {
        console.warn('Unexpected session response structure:', data)
        return rejectWithValue('Invalid session data received')
      }
    } catch (error: any) {
      console.error('loadOpsChatSession error:', error)
      console.error('Error response:', error.response?.data)
      if (error.response?.status === 403) {
        return rejectWithValue('Access denied. Operations Agent requires System Administrator privileges.')
      }
      return rejectWithValue(error.response?.data?.error || 'Failed to load session')
    }
  }
)

export const createOpsChatSession = createAsyncThunk<OpsChatSession, void>(
  'opsChat/createSession',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiClient.post('/ops/chat-sessions')
      const data = getResponseData(response)
      console.log('createOpsChatSession full response:', response)
      console.log('createOpsChatSession response.data:', data)
      console.log('createOpsChatSession response.data.data:', data?.data)
      
      // Handle different response structures
      if (data?.data) {
        return data.data
      } else if (data && typeof data === 'object' && data.id) {
        return data
      } else {
        console.warn('Unexpected create session response structure:', data)
        return rejectWithValue('Invalid session data received')
      }
    } catch (error: any) {
      console.error('createOpsChatSession error:', error)
      console.error('Error response:', error.response?.data)
      if (error.response?.status === 403) {
        return rejectWithValue('Access denied. Operations Agent requires System Administrator privileges.')
      }
      return rejectWithValue(error.response?.data?.error || 'Failed to create session')
    }
  }
)

export const sendOpsMessage = createAsyncThunk<
  OpsMessage,
  { 
    content: string
    sessionId?: string | null
  }
>(
  'opsChat/sendMessage',
  async ({ content, sessionId }, { rejectWithValue }) => {
    try {
      const response = await apiClient.post('/ops/execute-query', {
        question: content,
        context: sessionId ? { sessionId } : undefined
      })
      const data = getResponseData(response)
      return data.data
    } catch (error: any) {
      if (error.response?.status === 403) {
        return rejectWithValue('Access denied. Operations Agent requires System Administrator privileges.')
      }
      return rejectWithValue(error.response?.data?.error || 'Failed to send message')
    }
  }
)

export const updateOpsChatSession = createAsyncThunk<
  OpsChatSession,
  { sessionId: string; updates: Partial<OpsChatSession> }
>(
  'opsChat/updateSession',
  async ({ sessionId, updates }, { rejectWithValue }) => {
    try {
      const response = await apiClient.put(`/ops/chat-sessions/${sessionId}`, updates)
      const data = getResponseData(response)
      console.log('updateOpsChatSession full response:', response)
      console.log('updateOpsChatSession response.data:', data)
      console.log('updateOpsChatSession response.data.data:', data?.data)
      
      // Handle different response structures
      if (data?.data) {
        return data.data
      } else if (data && typeof data === 'object' && data.id) {
        return data
      } else {
        console.warn('Unexpected update session response structure:', data)
        return rejectWithValue('Invalid session data received')
      }
    } catch (error: any) {
      console.error('updateOpsChatSession error:', error)
      console.error('Error response:', error.response?.data)
      if (error.response?.status === 403) {
        return rejectWithValue('Access denied. Operations Agent requires System Administrator privileges.')
      }
      return rejectWithValue(error.response?.data?.error || 'Failed to update session')
    }
  }
)

export const deleteOpsChatSession = createAsyncThunk<void, string>(
  'opsChat/deleteSession',
  async (sessionId: string) => {
    await apiClient.delete(`/ops/chat-sessions/${sessionId}`)
  }
)

const opsChatSlice = createSlice({
  name: 'opsChat',
  initialState,
  reducers: {
    setActiveSession: (state, action: PayloadAction<string | null>) => {
      state.activeSessionId = action.payload
    },
    
    addOptimisticMessage: (state, action: PayloadAction<{ sessionId: string; message: OpsMessage }>) => {
      const { sessionId, message } = action.payload
      
      // Ensure session exists before trying to access it
      if (!state.sessions[sessionId]) {
        state.sessions[sessionId] = {
          id: sessionId,
          title: null,
          isActive: true,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          messages: []
        }
      }
      
      // Double-check that messages array exists
      if (!state.sessions[sessionId].messages) {
        state.sessions[sessionId].messages = []
      }
      
      state.sessions[sessionId].messages.push(message)
    },

    removeOptimisticMessage: (state, action: PayloadAction<{ sessionId: string; messageId: string }>) => {
      const { sessionId, messageId } = action.payload
      if (state.sessions[sessionId] && state.sessions[sessionId].messages) {
        state.sessions[sessionId].messages = state.sessions[sessionId].messages.filter(
          msg => msg.id !== messageId
        )
      }
    },

    startStreamingMessage: (state, action: PayloadAction<{ sessionId: string; messageId: string }>) => {
      const { sessionId, messageId } = action.payload
      state.streaming.currentStreamingMessage = messageId
      
      if (!state.sessions[sessionId]) {
        state.sessions[sessionId] = {
          id: sessionId,
          title: null,
          isActive: true,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          messages: []
        }
      }

      // Ensure messages array exists
      if (!state.sessions[sessionId].messages) {
        state.sessions[sessionId].messages = []
      }

      // Check if message already exists to prevent duplicates
      const existingMessage = state.sessions[sessionId].messages.find(msg => msg.id === messageId)
      if (existingMessage) {
        return
      }

      // Add streaming message placeholder
      const streamingMessage: OpsMessage = {
        id: messageId,
        role: 'ASSISTANT',
        content: '',
        createdAt: new Date().toISOString(),
        isStreaming: true
      }
      state.sessions[sessionId].messages.push(streamingMessage)
    },

    addStreamingChunk: (state, action: PayloadAction<{ 
      sessionId: string; 
      messageId: string; 
      chunk: string; 
    }>) => {
      const { sessionId, messageId, chunk } = action.payload
      if (state.sessions[sessionId] && state.sessions[sessionId].messages && Array.isArray(state.sessions[sessionId].messages)) {
        const message = state.sessions[sessionId].messages.find(msg => msg.id === messageId)
        if (message) {
          message.content += chunk
          message.isStreaming = true
        }
      }
    },

    updateStreamingMessage: (state, action: PayloadAction<{ 
      sessionId: string; 
      messageId: string; 
      content: string; 
      isComplete?: boolean;
      metadata?: any;
    }>) => {
      const { sessionId, messageId, content, isComplete, metadata } = action.payload
      if (state.sessions[sessionId] && state.sessions[sessionId].messages && Array.isArray(state.sessions[sessionId].messages)) {
        const message = state.sessions[sessionId].messages.find(msg => msg.id === messageId)
        if (message) {
          message.content = content
          message.isStreaming = !isComplete
          if (metadata) {
            message.metadata = metadata
          }
          if (isComplete) {
            state.streaming.currentStreamingMessage = null
          }
        }
      }
    },

    streamingCompleted: (state, action: PayloadAction<{ messageId: string }>) => {
      const { messageId } = action.payload
      state.streaming.currentStreamingMessage = null
      
      // Find and ensure the message is marked as not streaming
      Object.values(state.sessions).forEach(session => {
        if (session && session.messages && Array.isArray(session.messages)) {
          const message = session.messages.find(msg => msg.id === messageId)
          if (message) {
            message.isStreaming = false
          }
        }
      })
    },

    streamingError: (state, action: PayloadAction<{ messageId: string; error: string }>) => {
      const { messageId, error } = action.payload
      state.streaming.currentStreamingMessage = null
      
      // Find and update the message with error
      Object.values(state.sessions).forEach(session => {
        if (session && session.messages && Array.isArray(session.messages)) {
          const message = session.messages.find(msg => msg.id === messageId)
          if (message) {
            message.isStreaming = false
            message.error = error
          }
        }
      })
    },

    transferTempSession: (state, action: PayloadAction<{ tempSessionId: string; realSessionId: string }>) => {
      const { tempSessionId, realSessionId } = action.payload
      
      // Transfer the temp session to the real session ID
      if (state.sessions[tempSessionId]) {
        state.sessions[realSessionId] = {
          ...state.sessions[tempSessionId],
          id: realSessionId
        }
        delete state.sessions[tempSessionId]
      }
      
      // Update active session ID
      if (state.activeSessionId === tempSessionId) {
        state.activeSessionId = realSessionId
      }
    },

    cancelStreaming: (state, action: PayloadAction<{ messageId: string }>) => {
      const { messageId } = action.payload
      state.streaming.currentStreamingMessage = null
      
      // Mark message as cancelled
      Object.values(state.sessions).forEach(session => {
        if (session && session.messages && Array.isArray(session.messages)) {
          const message = session.messages.find(msg => msg.id === messageId)
          if (message) {
            message.isStreaming = false
            message.error = 'Response generation was cancelled'
          }
        }
      })
    },

    addChartToMessage: (state, action: PayloadAction<{ 
      sessionId: string
      messageId: string
      chart: ChartData
    }>) => {
      const { sessionId, messageId, chart } = action.payload
      
      if (state.sessions[sessionId] && state.sessions[sessionId].messages) {
        const message = state.sessions[sessionId].messages.find(msg => msg.id === messageId)
        if (message) {
          if (!message.charts) {
            message.charts = []
          }
          message.charts.push(chart)
        }
      }
    },

    addHierarchicalTableToMessage: (state, action: PayloadAction<{ 
      sessionId: string
      messageId: string
      table: HierarchicalTableData
    }>) => {
      const { sessionId, messageId, table } = action.payload
      
      if (state.sessions[sessionId] && state.sessions[sessionId].messages) {
        const message = state.sessions[sessionId].messages.find(msg => msg.id === messageId)
        if (message) {
          if (!message.hierarchicalTables) {
            message.hierarchicalTables = []
          }
          message.hierarchicalTables.push(table)
        }
      }
    },

    clearSessions: (state) => {
      state.sessions = {}
      state.activeSessionId = null
      state.streaming.currentStreamingMessage = null
    }
  },

  extraReducers: (builder) => {
    builder
      // Fetch sessions
      .addCase(fetchOpsChatSessions.pending, (state) => {
        state.loadingSessions = true
        state.sessionsError = null
      })
      .addCase(fetchOpsChatSessions.fulfilled, (state, action) => {
        state.loadingSessions = false
        const sessions = action.payload
        
        // Handle case where sessions might be undefined or null
        if (Array.isArray(sessions)) {
          state.sessions = sessions.reduce((acc, session) => {
            acc[session.id] = session
            return acc
          }, {} as Record<string, OpsChatSession>)
        } else {
          console.warn('fetchOpsChatSessions returned non-array data:', sessions)
          state.sessions = {}
        }
      })
      .addCase(fetchOpsChatSessions.rejected, (state, action) => {
        state.loadingSessions = false
        state.sessionsError = action.error.message || 'Failed to fetch sessions'
      })

      // Load session
      .addCase(loadOpsChatSession.pending, (state, action) => {
        const sessionId = action.meta.arg
        if (state.sessions[sessionId]) {
          state.sessions[sessionId].isLoading = true
          state.sessions[sessionId].error = undefined
        }
      })
      .addCase(loadOpsChatSession.fulfilled, (state, action) => {
        const session = action.payload
        console.log('loadOpsChatSession.fulfilled payload:', session)
        
        // Ensure session has required properties
        if (session && session.id) {
          // Store the session with messages (charts are already included from backend)
          state.sessions[session.id] = {
            ...session,
            messages: session.messages || [],
            isLoading: false,
            error: undefined
          }
          
          // Log chart restoration for debugging
          if (session.messages && Array.isArray(session.messages)) {
            const messagesWithCharts = session.messages.filter((message: any) => 
              message.charts && Array.isArray(message.charts) && message.charts.length > 0
            )
            if (messagesWithCharts.length > 0) {
              console.log(`📊 Chart restoration: Loaded session ${session.id} with ${messagesWithCharts.length} messages containing charts`)
              messagesWithCharts.forEach((message: any) => {
                console.log(`📊 Message ${message.id} has ${message.charts.length} charts: ${message.charts.map((c: any) => c.title).join(', ')}`)
              })
            }
          }
        } else {
          console.error('Invalid session data in loadOpsChatSession.fulfilled:', session)
        }
      })
      .addCase(loadOpsChatSession.rejected, (state, action) => {
        const sessionId = action.meta.arg
        if (state.sessions[sessionId]) {
          state.sessions[sessionId].isLoading = false
          state.sessions[sessionId].error = action.error.message || 'Failed to load session'
        }
      })

      // Create session
      .addCase(createOpsChatSession.fulfilled, (state, action) => {
        const session = action.payload
        console.log('createOpsChatSession.fulfilled payload:', session)
        
        // Ensure session has required properties
        if (session && session.id) {
          state.sessions[session.id] = {
            ...session,
            messages: session.messages || []
          }
          state.activeSessionId = session.id
        } else {
          console.error('Invalid session data in createOpsChatSession.fulfilled:', session)
        }
      })

      // Update session
      .addCase(updateOpsChatSession.fulfilled, (state, action) => {
        const session = action.payload
        console.log('updateOpsChatSession.fulfilled payload:', session)
        
        // Ensure session has required properties
        if (session && session.id) {
          state.sessions[session.id] = {
            ...session,
            messages: session.messages || []
          }
        } else {
          console.error('Invalid session data in updateOpsChatSession.fulfilled:', session)
        }
      })

      // Delete session
      .addCase(deleteOpsChatSession.fulfilled, (state, action) => {
        const sessionId = action.meta.arg
        delete state.sessions[sessionId]
        if (state.activeSessionId === sessionId) {
          state.activeSessionId = null
        }
      })
  }
})

export const {
  setActiveSession,
  addOptimisticMessage,
  removeOptimisticMessage,
  startStreamingMessage,
  addStreamingChunk,
  updateStreamingMessage,
  streamingCompleted,
  transferTempSession,
  streamingError,
  cancelStreaming,
  addChartToMessage,
  addHierarchicalTableToMessage,
  clearSessions
} = opsChatSlice.actions

export default opsChatSlice.reducer